import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  REAL_ESTATE_DEPARTMENTS, FUNDING_TYPES, DEPARTMENTS, STATES, PROJECT_REASONS,
} from "./real-estate-form-options";

export interface RealEstatePrintData {
  form: any;
  projectId: string;
  status: string;
  owner: any;
  assignment: any;
  approvers: any[];
  attachments: any[];
  accountingCodeBlock?: any;
}

const labels: { [key: string]: string } = {
  fm: "FM", pm: "PM", myrp: "MYRP", om: "O&M", capital: "Capital",
  adaptiveId: "Adaptive ID", projectId: "Project ID",
  assetLocationId: "Asset Location ID", majorLocationId: "Major Location ID",
  landUnitId: "Land Unit ID", propertySiteId: "Property Site ID", assetId: "Asset ID",
  zip: "ZIP Code", lria: "LRIA", uopDetails: "UOP",
  uopDesc: "Unit of Property Description", uopRemovalMaterial: "Removal Material",
  projectReason: "Project Reason",
  serviceDate: "Estimated In-Service Date", actualInServiceDate: "Actual In-Service Date",
  omOperatingUnits: "O&M Operating Units", omAccountNum: "O&M Account Number",
  capitalCwipAccountNum: "Capital CWIP Account Number",
  capitalRwipAccountNum: "Capital RWIP Account Number",
  estimatedAfudcOfProject: "Estimated AFUDC of Project", afudcPercentage: "AFUDC Percentage",
  estimateCostOfRequest: "Estimated Cost of Request", previouslyApproved: "Previously Approved",
  currentEstAFUDC: "Current Estimated AFUDC", previousEstAFUDC: "Previous Estimated AFUDC",
  totalEstAFUDC: "Total Estimated AFUDC",
  currentEstContingency: "Current Contingency", previousEstContingency: "Previous Contingency",
  totalEstContingency: "Total Contingency", currentEstOM: "Current Estimated O&M",
  previousEstOM: "Previous Estimated O&M", totalEstOM: "Total Estimated O&M",
  currentEstCapCost: "Current Estimated Capital Cost",
  previousEstCapCost: "Previous Estimated Capital Cost", totalEstCapCost: "Total Estimated Capital Cost",
  currentTotalNoAFUDC: "Current Total Excluding AFUDC",
  previousTotalNoAFUDC: "Previous Total Excluding AFUDC",
  totalTotalNoAFUDC: "Total Excluding AFUDC",
  currentTotalWithAFUDC: "Current Total Including AFUDC",
  previousTotalWithAFUDC: "Previous Total Including AFUDC",
  totalTotalWithAFUDC: "Total Including AFUDC",
  totalTotalCapital: "Total Capital", dukeLabor: "Duke Labor",
  fundingProject: "Funding ID", doaAuthorityAmount: "DOA Authority Amount",
  expenditureSummary: "Summary of Expenditures", chargeSummary: "Summary of Charges",
  laborOM: "Labor O&M", contractorOM: "Contractor O&M", materialOM: "Material O&M",
  otherOM: "Other O&M", approvedOM: "Approved O&M",
  activityId: "Activity ID", operatingUnitId: "Operating Unit ID",
  workTask: "Construction/Removal Work Task", expenditureType: "Type of Expenditure",
  detailProjectId: "Detail Project ID", uopProject: "Unit of Property for Project",
  uopInstallMaterial: "Install Material", uopInstallLabor: "Install Labor",
  uopRemovalLabor: "Removal Labor", uopVintageYear: "Vintage Year / Useful Life",
  realEstateDepartment: "Real Estate Department", fundingType: "Funding Type",
  projectManager: "Project Manager (PM)", contractAdmin: "Contract Admin (JLL PC/FC)",
  assetContact: "Asset Forum Contact", startDate: "Project Start Date",
  completionDate: "Project Estimated Completion Date", omExpenditure: "O&M Expenditure",
  name: "Name, Title",
};

const currencyFields = [
  "purchasePrice", "salesProceeds", "appraisal", "survey", "title", "closingCost",
  "legalFees", "brokerMarketingFees", "environmentalReview", "dukeLabor",
  "buildingDemolition", "environmentalCleanup", "bookValue", "previouslyApproved",
  "doaAuthorityAmount", "currentEstOM", "previousEstOM", "totalEstOM",
];

function label(key: string): string {
  return labels[key] || key.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\bOM\b/g, "O&M").replace(/^./, character => character.toUpperCase());
}

function person(value: any): string {
  if (!value) return "";
  if (typeof value !== "object") return String(value);
  return value.displayName || [value.givenName, value.surname].filter(Boolean).join(" ") ||
    value.name || value.description || value.viewValue || value.email || value.mail || "";
}

function format(value: any, key: string): string {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map(entry => format(entry, key)).join(", ") || "-";
  const options = {
    realEstateDepartment: REAL_ESTATE_DEPARTMENTS, fundingType: FUNDING_TYPES,
    department: DEPARTMENTS, state: STATES, projectReason: PROJECT_REASONS,
  }[key];
  const option = options && options.find(entry => entry.value === value);
  if (option) return option.viewValue;
  if (/Date$/.test(key) || key === "date") {
    const date = new Date(typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
    if (!isNaN(date.getTime())) return key === "date" ? date.toLocaleString("en-US") : date.toLocaleDateString("en-US");
  }
  if (typeof value === "object") {
    return [person(value), value.jobTitle, value.mail || value.email].filter(Boolean).join(", ") || "-";
  }
  if (key === "afudcPercentage") return `${value}%`;
  if (currencyFields.indexOf(key) !== -1 || /cost|total|afudc|^(labor|contractor|material|other|approved)(CapitalAdditions|CostOfRemoval|OM|Total)$|^uop(Install|Removal)/i.test(key)) {
    const amount = Number(String(value).replace(/[$,]/g, ""));
    if (isFinite(amount)) {
      return (key === "salesProceeds" ? -Math.abs(amount) : amount)
        .toLocaleString("en-US", { style: "currency", currency: "USD" });
    }
  }
  return String(value);
}

function fields(values: any): any[] {
  const nodes = [];
  const cells = [];
  Object.keys(values || {}).forEach(key => {
    const value = values[key];
    if (Array.isArray(value) && value.some(entry => entry && typeof entry === "object")) {
      value.forEach((entry, index) => nodes.push({
        stack: [{ text: `${label(key)} ${index + 1}`, style: "subheading" }].concat(fields(entry)),
        margin: [0, 8, 0, 8],
      }));
    } else if (value && typeof value === "object" && !(value instanceof Date) && !person(value)) {
      nodes.push({ stack: [{ text: label(key), style: "subheading" }].concat(fields(value)), margin: [0, 8, 0, 8] });
    } else {
      cells.push({ stack: [
        { text: label(key), style: "fieldLabel" },
        { text: format(value, key), margin: [0, 4, 0, 8] },
      ] });
    }
  });
  if (cells.length) {
    const rows = [];
    for (let index = 0; index < cells.length; index += 3) {
      const row = cells.slice(index, index + 3);
      while (row.length < 3) row.push({ text: "" });
      rows.push(row);
    }
    nodes.unshift({
      table: { widths: ["*", "*", "*"], body: rows },
      layout: "lightHorizontalLines", margin: [0, 4, 0, 10],
    });
  }
  return nodes;
}

function section(title: string, values: any, nodes?: any[]): any {
  return { stack: [{ text: title, style: "section", headlineLevel: 1 }].concat(nodes || fields(values)), margin: [0, 12, 0, 4] };
}

function fieldCell(values: any, key: string): any {
  if (!key) return { text: "" };
  const value = (values || {})[key];
  if (typeof value === "boolean") return { text: `${value ? "[x]" : "[ ]"} ${label(key)}`, margin: [0, 4, 0, 8] };
  return { stack: [
    { text: label(key), style: "fieldLabel" },
    { text: format(value, key), margin: [0, 4, 0, 8] },
  ] };
}

function fieldGrid(values: any, rows: any[][]): any {
  return {
    table: { widths: ["*", "*", "*"], body: rows.map(row => row.map(key =>
      Array.isArray(key) ? { stack: key.map(entry => fieldCell(values, entry)) } : fieldCell(values, key))),
    },
    layout: "lightHorizontalLines", margin: [0, 4, 0, 10],
  };
}

function heading(text: string): any {
  return { text, style: "subheading", headlineLevel: 1 };
}

function textArea(key: string, value: any): any[] {
  return [heading(label(key)), {
    table: { widths: ["*"], body: [[{ text: format(value, key), margin: [6, 6, 6, 6] }]] },
    layout: { hLineColor: () => "#26bcd7", vLineColor: () => "#26bcd7" },
    margin: [0, 4, 0, 10],
  }];
}

function printTable(headers: string[], rows: any[][], widths?: any[], compact = false): any {
  return {
    table: {
      headerRows: 1, widths: widths || headers.map(() => "*"),
      body: [headers.map(text => ({ text, style: "tableHeader", fillColor: "#f3fafd" }))]
        .concat(rows.length ? rows : [headers.map(() => ({ text: "-" }))]),
    },
    fontSize: compact ? 11 : 12, layout: "lightHorizontalLines", margin: [0, 4, 0, 12],
  };
}

function entryTable(entries: any[], keys: string[], widths?: any[], compact = false): any {
  return printTable(keys.map(label), (entries || []).map(entry => keys.map(key => ({ text: format(entry[key], key) }))), widths, compact);
}

function detailLayout(values: any, title: any, landServices: boolean): any[] {
  const detail = values || {};
  const locationRows: any[][] = landServices ? [
    ["state", "department", "projectId"],
    ["assetLocation", "majorLocation", title.acquisition ? "streetAddress" : "landUnitId"],
  ] : [
    ["state", "department", title.fm ? "" : "adaptiveId"],
    ["projectId", "building", "assetLocation"], ["majorLocation", "city", "businessUnit"],
    ["fundingProject", "projectType", "projectClass"],
    ["isLocationLeased", detail.isLocationLeased ? "leaseStartDate" : "", detail.isLocationLeased ? "leaseEndDate" : ""],
  ];
  if (landServices && title.acquisition) locationRows.push(
    ["city", "zip", "county"], ["town", "", ""],
    ["propertySiteId", "landPerUnit", "numberOfAcresBeingAdded"],
    ["numberOfBuildingsBeingAdded", "businessUnit", "fundingProject"],
    ["projectType", "locationType", "businessSegment"], ["projectClass", "", ""],
  );
  if (landServices && title.disposal) locationRows.push(
    ["numberOfAcresDisposed", "landPerUnit", "bookValue"],
    [["partialDisposal", "fullDisposal"], "lria", "businessUnit"],
    ["fundingProject", "projectType", "businessSegment"], ["projectClass", "assetId", "propertySiteId"],
  );
  const description = textArea("projectDescription", detail.projectDescription);
  const nodes = landServices ? description.concat([fieldGrid(detail, locationRows)])
    : [fieldGrid(detail, locationRows)].concat(description);
  nodes.push(heading("Contacts"), fieldGrid(detail, landServices
    ? [["projectManager", "sponsor", "assetContact"]]
    : [["projectManager", "sponsor", "contractAdmin"], ["assetContact", "additionalContact", ""]]));
  const timelineRows = [["startDate", "serviceDate", "completionDate"]];
  if (detail.actualInServiceDate) timelineRows.push(["actualInServiceDate", "", ""]);
  nodes.push(heading("Timeline"), fieldGrid(detail, timelineRows));
  return nodes;
}

function realEstateEstimateLayout(values: any): any[] {
  const estimate = values || {};
  const charge = estimate.chargeSummary || {};
  const funding = estimate.expenditureSummary || {};
  const chargeRows = [
    ["labor", "Labor (Duke Energy Labor)"], ["contractor", "Contractor"],
    ["material", "Materials"], ["other", "Other"], ["approved", "Total Project Approved Funding"],
  ].map(row => [{ text: row[1] }].concat(["CapitalAdditions", "CostOfRemoval", "OM", "Total"]
    .map(suffix => ({ text: format(charge[row[0] + suffix], row[0] + suffix), alignment: "right" }))));
  const fundingRows = [
    ["EstCapCost", "Estimated Capital Cost"], ["EstAFUDC", "Estimated AFUDC"],
    ["TotalCapital", "Total Capital"], ["EstOM", "Estimated O&M Costs"],
    ["TotalNoAFUDC", "Total Excluding AFUDC"], ["TotalWithAFUDC", "Total Including AFUDC"],
  ].map(row => [{ text: row[1] }].concat(["previous", "current", "total"]
    .map(prefix => ({ text: format(funding[prefix + row[0]], prefix + row[0]), alignment: "right" }))));
  return [
    entryTable(estimate.estimates, ["workTask", "expenditureType", "estimatedCost", "detailProjectId", "activityId",
      "operatingUnitId", "processCode", "responsibilityCenter", "accountNumber", "chargeType", "projectChargeIndicator"],
      [94, 62, 64, 54, 58, 50, 42, 60, 52, 47, 66], true),
    heading("Charge Type Summary"),
    printTable(["Charge Type Summary", "Capital Additions", "Capital - Cost of Removal", "O&M", "Total"], chargeRows),
    heading("Summary of Expenditures"),
    printTable(["Summary", "Previous Funding", "Current Funding Request", "Total Project Funding"], fundingRows),
  ];
}

function landServicesEstimateLayout(values: any): any[] {
  const estimate = values || {};
  const costKeys = ["purchasePrice", "salesProceeds", "appraisal", "survey", "title", "closingCost", "legalFees",
    "brokerMarketingFees", "environmentalReview", "dukeLabor", "buildingDemolition", "environmentalCleanup", "total"];
  const summaryTable = group => {
    const summary = estimate[group] || {};
    return { stack: [heading(label(group)), printTable(["Summary", "Amount"], Object.keys(summary).map(key => [
      { text: label(key) }, { text: format(summary[key], key), alignment: "right" },
    ]), ["*", 120])] };
  };
  return [
    heading("Expenditure Type"),
    printTable(["Expenditure Type", "Amount"], costKeys.filter(key => Object.prototype.hasOwnProperty.call(estimate, key))
      .map(key => [{ text: label(key) }, { text: format(estimate[key], key), alignment: "right" }]), ["*", 140]),
    fieldGrid(estimate, [["expenditureType", "utilityAccount", "retirementUnit"], ["chargeType", "transactionType", ""]]),
    heading("Summary of Expenditures"),
    { columns: [summaryTable("capital"), summaryTable("om")], columnGap: 20 },
    { text: `Total Project Cost: ${format(estimate.totalProjectCost, "totalProjectCost")}`, bold: true, alignment: "right", margin: [0, 4, 0, 8] },
  ];
}

function omit(values: any, keys: string[]): any {
  const result = {};
  Object.keys(values || {}).filter(key => keys.indexOf(key) === -1)
    .forEach(key => result[key] = values[key]);
  return result;
}

export function buildRealEstatePrintDocument(data: RealEstatePrintData): any {
  const form = data.form;
  const landServices = form.titleForm.realEstateDepartment === "LAND_SERVICES";
  const content: any[] = [
    { text: `${landServices ? "Land Services" : "Real Estate"} Funding Request`, style: "heading" },
    { text: `Form #${data.projectId || "-"}  |  ${data.status || "-"}`, margin: [0, 4, 0, 12] },
    section("Form Admin", null, [fieldGrid({ formOwner: person(data.owner), currentAssignment: person(data.assignment) },
      [["formOwner", "currentAssignment", ""]])]),
    section("Request Information", null, [fieldGrid(form.titleForm, landServices
      ? [["realEstateDepartment", ["acquisition", "disposal"], ["land", "building"]],
        ["region", ["estimate", "projectDate", "changeInScope"], ["capitalExpenditure", "omExpenditure"]]]
      : [["realEstateDepartment", "fundingType", ["fm", "pm", "myrp"]]])]),
  ];
  if (landServices) {
    const estimate = omit(form.landServicesEstimateDetailsForm, [
      !form.titleForm.acquisition ? "purchasePrice" : "", !form.titleForm.disposal ? "salesProceeds" : "",
    ]);
    if (!form.titleForm.acquisition) {
      estimate.capital = { total: estimate.capital && estimate.capital.total };
      estimate.om = { total: estimate.om && estimate.om.total };
    } else if (form.titleForm.region === "DE Florida") {
      estimate.capital = omit(estimate.capital, ["afudcPercentage"]);
    }
    content.push(section("Part A: Project Detail Information", null, detailLayout(form.landServicesDetailForm, form.titleForm, true)));
    content.push(section("Part B: Project Estimate Details", null, landServicesEstimateLayout(estimate)));
    const accounting = form.landServicesAccountingForm || {};
    content.push(section("Part C: Accounting Codeblock", null, ["capital", "om"].reduce((nodes, group) => nodes.concat([
      heading(label(group)), fieldGrid(accounting[group], [
        ["businessUnit", "responsibilityCenter", "operatingUnit"], ["account", "processCode", "projectId"],
        ["activityId", "resourceType", "projectChargeIndicator"],
      ]),
    ]), [])));
  } else {
    content.push(section("Part A: Project Detail Information", null, detailLayout(form.detailForm, form.titleForm, false)));
    const justification = form.justificationForm || {};
    content.push(section("Part B: Project Justification", null, textArea("projectJustification", justification.projectJustification).concat([
      heading("Reason for Project"), ...PROJECT_REASONS.map(reason => ({
        text: `${(justification.projectReason || []).indexOf(reason.value) !== -1 ? "[x]" : "[ ]"} ${reason.viewValue}`,
        margin: [0, 2, 0, 4],
      })),
    ])));
    const uops = form.uopForm && form.uopForm.uopDetails || [];
    const totalUopCost = uops.reduce((total, entry) => total + ["uopInstallMaterial", "uopInstallLabor", "uopRemovalLabor"]
      .reduce((sum, key) => sum + (Number(String(entry[key] || 0).replace(/[$,]/g, "")) || 0), 0), 0);
    content.push(section("Part C: Capital Unit of Property (UOP) Details", null, [
      entryTable(uops, ["uopProject", "uopDesc", "uopInstallMaterial", "uopInstallLabor", "uopRemovalLabor", "uopVintageYear"],
        [100, "*", 100, 100, 100, 110]),
      { text: `Total: ${format(totalUopCost, "total")}`, bold: true, alignment: "right" },
    ]));
    content.push(section("Part D: Project Estimate Details & Accounting Codeblock", null, realEstateEstimateLayout(form.estimateForm)));
  }
  content.push(section(`Part ${landServices ? "D" : "E"}: Project Funding Approval`, null, [
    entryTable(data.approvers, ["name", "doaAuthorityAmount"], ["*", 200]),
  ]));
  if (data.accountingCodeBlock && Object.keys(data.accountingCodeBlock).length) {
    content.push(section("Saved Accounting Codeblock", data.accountingCodeBlock));
  }
  if (data.attachments && data.attachments.length) {
    content.push(section("Attachments", { files: data.attachments.map(file => ({ fileName: file.fileName || file.name })) }));
  }
  return {
    pageSize: { width: 841.89, height: "auto" }, pageMargins: [36, 36, 36, 40], content,
    defaultStyle: { font: "RealEstatePrint", fontSize: 12, color: "#333333" },
    styles: {
      heading: { fontSize: 18, bold: true, color: "#005984" },
      section: { fontSize: 14, bold: true, color: "#005984", margin: [0, 0, 0, 6] },
      subheading: { fontSize: 12, bold: true, margin: [0, 4, 0, 4] },
      fieldLabel: { fontSize: 11, bold: true, color: "#696969" },
      tableHeader: { fontSize: 11, bold: true, color: "#696969" },
    },
  };
}

export function printRealEstateFormPDF(data: RealEstatePrintData): void {
  const fonts = {
    RealEstatePrint: {
      normal: "Roboto-Regular.ttf", bold: "Roboto-Medium.ttf",
      italics: "Roboto-Italic.ttf", bolditalics: "Roboto-MediumItalic.ttf",
    },
  };
  pdfMake.createPdf(buildRealEstatePrintDocument(data), undefined, fonts, pdfFonts).print();
}